import { Button, notify } from '@affine/component';
import { FetchService, ServerService } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { IssueIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IntegrationSettingHeader } from '../setting';
import * as styles from './setting-panel.css';
import { LinearWorkspaceBinding } from './workspace-binding';

/**
 * One connected Linear workspace.
 *
 * A developer holds one per worX contract, so this panel is a LIST, unlike the
 * Google panel's single connected/not-connected toggle.
 */
interface LinearConnection {
  organizationId: string;
  organizationName: string | null;
  /**
   * `reconnect_required` means the grant is dead — revoked in Linear, or the
   * stored refresh token was rotated away. Only the developer can fix it.
   * `unavailable` means Linear could not be reached just now, which is
   * temporary and needs no action.
   */
  state: 'ok' | 'reconnect_required' | 'unavailable';
  connectedAt: string;
  expiresAt: string | null;
}

interface LinearStatus {
  /** False when LINEAR_CLIENT_ID never reached the server. */
  configured: boolean;
  connections: LinearConnection[];
}

const LinearSettingHeader = ({ action }: { action?: React.ReactNode }) => {
  const t = useI18n();
  return (
    <IntegrationSettingHeader
      icon={<IssueIcon />}
      name={t['com.affine.integration.linear.name']()}
      desc={t['com.affine.integration.linear.desc']()}
      action={action}
    />
  );
};

export const LinearSettingPanel = () => {
  const t = useI18n();
  const fetchService = useService(FetchService);
  const serverService = useService(ServerService);

  const [status, setStatus] = useState<LinearStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  // Guards a stale response from clobbering newer state — e.g. a refresh fired
  // just before unmount, or two disconnects in quick succession.
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      const res = await fetchService.fetch('/api/linear/status');
      const data: LinearStatus = await res.json();
      if (requestIdRef.current !== requestId) return;
      setStatus(data);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      // Deliberately leaves `status` null, which renders the Connect view
      // below: when the status call itself is what is broken, offering the
      // developer a way forward beats showing them a dead end.
      notify.error({
        title: t['com.affine.linear.connections.status-failed'](),
        message: error instanceof Error ? error.message : 'Please try again',
      });
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [fetchService, t]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh, t]);

  const handleConnect = useCallback(() => {
    // Full-page redirect rather than a popup. The popup the Google integration
    // uses exists because it also launches from the send-by-email compose
    // dialog, where navigating away would lose a draft; Linear only connects
    // from this settings page, so there is nothing to lose — and it avoids the
    // opener-less popup signalling that YOD-431 was filed against.
    //
    // ⚠️ On Electron this navigates the app window to linear.app. Web is the
    // shipped target; a desktop-native flow needs an IPC round trip and is not
    // attempted here.
    const url = new URL(
      '/api/linear/connect',
      serverService.server.serverMetadata.baseUrl
    ).toString();
    window.location.href = url;
  }, [serverService]);

  const handleDisconnect = useCallback(
    (organizationId: string) => {
      setDisconnecting(organizationId);
      fetchService
        .fetch('/api/linear/disconnect', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          // Always explicit. The backend refuses without it, precisely so a
          // developer with two contracts cannot have the wrong one severed.
          body: JSON.stringify({ organizationId }),
        })
        .then(() => {
          notify.success({
            title: t['com.affine.linear.connections.disconnected'](),
          });
          return refresh();
        })
        .catch((error: unknown) => {
          notify.error({
            title: t['com.affine.linear.connections.disconnect-failed'](),
            message:
              error instanceof Error ? error.message : 'Please try again',
          });
        })
        .finally(() => setDisconnecting(null));
    },
    [fetchService, refresh, t]
  );

  if (loading) {
    return <LinearSettingHeader />;
  }

  if (status && !status.configured) {
    return (
      <div>
        <LinearSettingHeader />
        <div className={styles.section}>
          <div className={styles.notConfigured}>
            {t['com.affine.linear.connections.not-configured']()}
          </div>
        </div>
      </div>
    );
  }

  const connections = status?.connections ?? [];

  return (
    <div>
      <LinearSettingHeader />

      {/* What this workspace tracks comes first: it is the question a
          developer opening this panel from inside a contract is asking. The
          account-level connection list below answers a different one. */}
      <LinearWorkspaceBinding connections={connections} />

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {t['com.affine.linear.connections.title']()}
        </div>
        {connections.length > 0 ? (
          <div className={styles.list}>
            {connections.map(conn => (
              <div key={conn.organizationId} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.workspaceName}>
                    {conn.organizationName ??
                      t['com.affine.linear.connections.unnamed-workspace']()}
                  </span>
                  {conn.state === 'reconnect_required' ? (
                    <span className={styles.rowDetailWarning}>
                      {t['com.affine.linear.connections.invalid']()}
                    </span>
                  ) : conn.state === 'unavailable' ? (
                    <span className={styles.rowDetail}>
                      {t['com.affine.linear.connections.unavailable']()}
                    </span>
                  ) : (
                    <span className={styles.rowDetail}>
                      {t['com.affine.linear.connections.connected']()}
                    </span>
                  )}
                </div>
                <Button
                  variant="error"
                  onClick={() => handleDisconnect(conn.organizationId)}
                  disabled={disconnecting === conn.organizationId}
                >
                  {t['com.affine.linear.connections.disconnect']()}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.hint}>
            {t['com.affine.linear.connections.intro']()}
          </div>
        )}

        <Button
          variant="primary"
          size="large"
          onClick={handleConnect}
          className={styles.connectButton}
          prefix={<IssueIcon />}
        >
          {connections.length > 0
            ? t['com.affine.linear.connections.connect-another']()
            : t['com.affine.linear.connections.connect']()}
        </Button>
      </div>
    </div>
  );
};
